import { Chain } from 'graphql/data/Token'
import { TokenPriceQuery } from 'graphql/data/TokenPrice'
import { useTokenDayDataPricesQuery } from 'graphql/thegraph/__generated__/types-and-hooks'
import { useFetchedCurrentTokenDatas } from 'graphql/thegraph/tokens/currenTokenData'
import gql from 'graphql-tag'
import { useTheGraphClient } from 'hooks/useTheGraphClient'

gql`
  query tokenDayDataPrices($tokenAddress: ID, $date: Int) {
    tokenDayDatas(where: { date_gte: $date, token_: { id: $tokenAddress } }) {
      id
      priceUSD
      date
    }
  }
`

/**
 * Fetch token price data
 */
export interface TokenPrices {
  id: string
  timestamp: number
  value: number
}

export function useTokenDayDataPrices(
  tokenAddress: string | undefined,
  chain: Chain
): {
  loading: boolean
  error: boolean
  tokenPriceQuery: TokenPriceQuery | undefined
} {
  if (!tokenAddress)
    return {
      loading: false,
      error: false,
      tokenPriceQuery: undefined,
    }

  const tokenAddressLowerCase = tokenAddress?.toLowerCase()

  const _30DayAgo = Math.round(Date.now() / 86400 / 1000) * 86400 - 86400 * 30
  const client = useTheGraphClient()

  const { data, loading, error } = useTokenDayDataPricesQuery({
    variables: {
      tokenAddress: tokenAddressLowerCase,
      date: _30DayAgo,
    },
    fetchPolicy: 'cache-and-network',
    client: client,
    errorPolicy: 'all',
  })

  const {
    data: currentTokenDataMap,
    loading: fetchLoading,
    error: errorLoading,
  } = useFetchedCurrentTokenDatas([tokenAddressLowerCase])

  const anyError = Boolean(error || errorLoading || !data || !currentTokenDataMap)
  const anyLoading = Boolean(loading || fetchLoading)
  if (anyError || anyLoading) {
    return {
      loading: anyLoading,
      error: anyError,
      tokenPriceQuery: undefined,
    }
  }

  const tokenDayDatas = data?.tokenDayDatas.map((token) => ({
    id: token.id?.split('-')[0],
    timestamp: token.date,
    value: Number(token.priceUSD),
  }))

  if (!tokenDayDatas || tokenDayDatas.length === 0) {
    const tokenPriceQuery: TokenPriceQuery = {
      token: {
        id: '',
        address: tokenAddress || '',
        chain,
        market: {
          id: tokenAddress || '',
          price: {
            id: tokenAddress,
            value: 0,
          },
          priceHistory: [
            {
              id: tokenAddress,
              value: 0,
              timestamp: Math.floor(new Date().getTime() / 1000),
            },
          ],
        },
      },
    }

    return {
      loading: false,
      error: false,
      tokenPriceQuery,
    }
  }

  const priceHistory = tokenDayDatas.map((token) => ({
    id: token.id,
    value: token.value,
    timestamp: token.timestamp,
  }))

  const currentPrice = currentTokenDataMap
    ? currentTokenDataMap[tokenAddressLowerCase]
      ? currentTokenDataMap[tokenAddressLowerCase].priceUSD
      : 0
    : 0

  const tokenPriceQuery: TokenPriceQuery = {
    token: {
      id: '',
      address: tokenDayDatas[0]?.id || '',
      chain,
      market: {
        id: tokenDayDatas[0]?.id || '',
        price: {
          id: tokenDayDatas[0]?.id,
          value: currentPrice,
        },
        priceHistory: priceHistory,
      },
    },
  }

  return {
    loading: loading,
    error: Boolean(error),
    tokenPriceQuery,
  }
}
